import React, { useState } from "react";
import {
  Alert,
  Box,
  Container,
  Typography,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  useTheme,
  alpha,
  Divider,
  Stack,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm, SubmitHandler } from "react-hook-form";
import {
  Visibility,
  VisibilityOff,
  SecurityOutlined,
  CheckCircleOutline,
  ErrorOutline,
  HourglassEmpty,
} from "@mui/icons-material";
import { keyframes } from "@emotion/react";
import api from "../../utils/api";
import { getLoggedinUserEmail } from "../../api/apiCore";
import passwordIcon from "../../assets/settings/settings_password.svg";
import { useDashboardTheme } from "../Dashboard/context/DashboardThemeContext";

const hourglassRotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

interface FormValues {
  oldpassword: string;
  password: string;
  confirmpassword: string;
}

const ChangePasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = useDashboardTheme();
  const textSecondary = isDark ? "rgba(255,255,255,0.5)" : "text.secondary";
  const cardBg = isDark
    ? `linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)`
    : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.tertiary.main, 0.02)} 100%)`;
  const inputSx: SxProps<Theme> = isDark
    ? {
        "& .MuiOutlinedInput-root": {
          color: "#fff",
          backgroundColor: "rgba(255,255,255,0.05)",
          "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
          "&:hover fieldset": { borderColor: "rgba(255,255,255,0.3)" },
          "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
        },
        "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" },
        "& .MuiInputBase-input": {
          color: "#fff",
          "&::placeholder": { color: "rgba(255,255,255,0.35)", opacity: 1 },
        },
        "& .MuiIconButton-root": { color: "rgba(255,255,255,0.7)" },
        "& .MuiFormHelperText-root": { color: "rgba(255,255,255,0.45)" },
        "& .MuiFormHelperText-root.Mui-error": {
          color: theme.palette.error.light,
        },
      }
    : {};
  const [showPassword, setShowPassword] = useState({
    oldpassword: false,
    password: false,
    confirmpassword: false,
  });
  const [loading, setLoading] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [changepasswordError, setChangepasswordError] = useState<string | null>(
    null,
  );

  const resolver = yupResolver(
    yup.object().shape({
      oldpassword: yup.string().required(t("Please Enter Old Password.")),
      password: yup
        .string()
        .required(t("Please Enter New Password."))
        .min(8, t("Password must be at least 8 characters"))
        .matches(
          /[A-Z]/,
          t("Password must contain at least one uppercase letter"),
        )
        .matches(
          /[@$!%*?&#]/,
          t("Password must contain at least one special character"),
        )
        .notOneOf(
          [yup.ref("oldpassword")],
          t("New Password must be different from Old Password"),
        ),
      confirmpassword: yup
        .string()
        .oneOf([yup.ref("password")], t("Passwords don't match"))
        .required(t("This value is required.")),
    }),
  );

  const defaultValues: FormValues = {
    oldpassword: "",
    password: "",
    confirmpassword: "",
  };

  const methods = useForm<FormValues>({
    defaultValues,
    resolver,
    context: { abortEarly: false },
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = methods;

  const onSubmitForm: SubmitHandler<FormValues> = async (values) => {
    setLoading(true);
    setChangepasswordError(null);
    try {
      const email = getLoggedinUserEmail();

      if (!email) {
        setChangepasswordError(t("Email not found. Please log in again"));
        return;
      }

      const response = await api.post("/api/change-password", {
        email: email,
        old_password: values.oldpassword,
        new_password: values.password,
      });

      if (response.status >= 200 && response.status < 300) {
        setPasswordChanged(true);
        reset();
        setTimeout(() => {
          setPasswordChanged(false);
        }, 5000);
      } else {
        const errorData = response.data;
        setChangepasswordError(
          errorData.message ||
            t("An error occurred while changing the password."),
        );
      }
    } catch (error: any) {
      let errorMessage = t("An error occurred while changing the password.");
      const status = error.response?.status;
      const errorCode = error.response?.data?.error;
      const serverMessage = error.response?.data?.message;

      switch (status) {
        case 401:
          switch (errorCode) {
            case "access_token cookie missing":
              errorMessage = t(
                "Session expired. Please log in again to change your password",
              );
              break;
            default:
              errorMessage = t(
                "Current password is incorrect. Please check and try again",
              );
              break;
          }
          break;
        case 400:
          errorMessage =
            serverMessage ||
            t("Invalid data. Please check the fields and try again");
          break;
        default:
          if (status >= 500) {
            errorMessage = t("Internal server error. Please try again later");
          } else if (serverMessage) {
            errorMessage = serverMessage;
          }
          break;
      }

      setChangepasswordError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = (field: keyof FormValues) => {
    setShowPassword((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmitForm)}
      sx={{
        maxWidth: 600,
        mx: "auto",
        display: "grid",
        gap: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: "center" }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 80,
            height: 80,
            mb: 2,
          }}
        >
          <img
            src={passwordIcon}
            alt="Password"
            style={{
              width: "80px",
              height: "80px",
              objectFit: "contain",
            }}
          />
        </Box>
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1 }}
        >
          {t("setting.password")}
        </Typography>
        <Typography variant="body2" sx={{ color: textSecondary }}>
          {t("Change your password to keep your account secure")}
        </Typography>
      </Box>

      {/* Alerts */}
      <Stack spacing={2}>
        {changepasswordError && (
          <Alert
            severity="error"
            icon={<ErrorOutline />}
            sx={{
              borderRadius: 2,
              border: "none",
              background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
              "& .MuiAlert-message": {
                fontWeight: 500,
              },
            }}
          >
            {changepasswordError}
          </Alert>
        )}

        {passwordChanged && (
          <Alert
            severity="success"
            icon={<CheckCircleOutline />}
            sx={{
              borderRadius: 2,
              border: "none",
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
              "& .MuiAlert-message": {
                fontWeight: 500,
              },
            }}
          >
            {t("Your password has been changed successfully")}
          </Alert>
        )}
      </Stack>

      {/* Form Fields */}
      <Stack spacing={3}>
        {/* Senha Atual */}
        <Box>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 600,
              color: theme.palette.primary.main,
            }}
          >
            {t("Current Password")}
          </Typography>
          <TextField
            placeholder={t("Enter your current password")}
            type={showPassword.oldpassword ? "text" : "password"}
            fullWidth
            {...register("oldpassword")}
            error={!!errors.oldpassword}
            helperText={errors.oldpassword?.message || ""}
            variant="outlined"
            sx={inputSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleClickShowPassword("oldpassword")}
                    sx={{ color: theme.palette.primary.main }}
                  >
                    {showPassword.oldpassword ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* New Password and Confirmation */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontWeight: 600,
                color: theme.palette.primary.main,
              }}
            >
              {t("New Password")}
            </Typography>
            <TextField
              placeholder={t("Enter your new password")}
              type={showPassword.password ? "text" : "password"}
              fullWidth
              {...register("password")}
              error={!!errors.password}
              helperText={errors.password?.message || ""}
              variant="outlined"
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => handleClickShowPassword("password")}
                      sx={{ color: theme.palette.primary.main }}
                    >
                      {showPassword.password ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontWeight: 600,
                color: theme.palette.primary.main,
              }}
            >
              {t("Confirm New Password")}
            </Typography>
            <TextField
              placeholder={t("Confirm your new password")}
              type={showPassword.confirmpassword ? "text" : "password"}
              fullWidth
              {...register("confirmpassword")}
              error={!!errors.confirmpassword}
              helperText={errors.confirmpassword?.message || ""}
              variant="outlined"
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => handleClickShowPassword("confirmpassword")}
                      sx={{ color: theme.palette.primary.main }}
                    >
                      {showPassword.confirmpassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        {/* Password Requirements */}
        <Card
          sx={{
            background: cardBg,
            border: isDark
              ? "1px solid rgba(255,255,255,0.08)"
              : `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <SecurityOutlined
                sx={{ mr: 1, color: theme.palette.primary.main, fontSize: 20 }}
              />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: theme.palette.primary.main }}
              >
                {t("Password Requirements")}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ fontSize: "0.875rem", color: textSecondary }}
            >
              • {t("Minimum 8 characters")}
              <br />• {t("At least one uppercase letter")}
              <br />• {t("At least one special character")}
              <br />• {t("Different from current password")}
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            type="submit"
            disabled={loading}
            sx={{
              fontWeight: 600,
              py: 1.5,
              borderRadius: 2,
            }}
          >
            {loading ? (
              <>
                <HourglassEmpty
                  sx={{
                    mr: 1,
                    animation: `${hourglassRotate} 1.5s linear infinite`,
                  }}
                />
                {t("Saving...")}
              </>
            ) : (
              t("Change Password")
            )}
          </Button>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Button
            variant="outlined"
            fullWidth
            size="large"
            onClick={() => navigate("/settings/profile")}
            sx={{
              fontWeight: 600,
              py: 1.5,
              borderRadius: 2,
              ...(isDark && {
                color: "rgba(255,255,255,0.85)",
                borderColor: "rgba(255,255,255,0.25)",
                "&:hover": {
                  borderColor: "rgba(255,255,255,0.5)",
                  bgcolor: "rgba(255,255,255,0.05)",
                },
              }),
            }}
          >
            {t("Cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ChangePasswordPage;
